import React from 'react';
import styles from './SectorIndustryOutlookV2.module.scss';
import GrowArrow from '../../../../../../assets/Svg/GrowArrow';
import greenTick from '../../../../../../assets/green-tick.svg';
import redInfoFull from '../../../../../../assets/red-i-full.svg';
import TopRightArrowIcon from '../../../../../../assets/Svg/TopRightArrowIcon';
import { SECTOR_INSIGHTS, ICON_STYLES } from './constants';
import AIGeneratedBadge from '../../../../../../common/AIGeneratedBadge/AIGeneratedBadge';
import HeaderBodyBox from '../../../../../../common/HeaderBodyBox/HeaderBodyBox';

interface Source {
    sourceName: string;
    url: string;
    dataPoint: string;
}

interface SectorOutlook {
    marketSize: string;
    industryCagr: string;
    sectorRisks: string;
    marketGrowth: string;
    sectorOverview: string;
    recentTrends: string[];
    growthDrivers: string[];
    keyRisks: string[];
    sources: Source[];
}

interface SectorIndustryOutlookV2Props {
    sectorOutlook: SectorOutlook;
    sectorName: string;
}

const SectorIndustryOutlookV2: React.FC<SectorIndustryOutlookV2Props> = ({ sectorOutlook, sectorName }) => {
    if (!sectorOutlook) {
        return null;
    }

    const renderSectorInsights = () => (
        <div className={styles.sectorInsights}>
            {SECTOR_INSIGHTS.map(({ title, key, isRiskIndicator }) => (
                <div key={key} className={styles.insightItem}>
                    <h3 className={styles.sectionTitle}>{title}</h3>
                    <p
                        className={`${isRiskIndicator ? styles.sectorRisks : styles.insightText} ${
                            isRiskIndicator ? styles[sectorOutlook[key]?.toLowerCase()] : ''
                        }`}
                    >
                        {sectorOutlook[key]}
                    </p>
                </div>
            ))}
        </div>
    );

    const renderTrendPill = (text: string, index: number) => (
        <div key={index} className={styles.trendPill}>
            <span style={{ marginRight: '8px' }}>
                <GrowArrow fill="#545B62" />
            </span>
            {text}
        </div>
    );

    const renderListItem = (text: string, index: number, icon: string, className: string) => (
        <li key={index} className={className}>
            <span style={ICON_STYLES.container}>
                <img style={ICON_STYLES.icon} src={icon} alt={`${icon} icon`} />
            </span>
            {text}
        </li>
    );

    const renderSourcePill = (source: Source, index: number) => (
        <a key={index} href={source.url} target="_blank" rel="noopener noreferrer" className={styles.sourcePill} title={source.sourceName}>
            <span style={ICON_STYLES.container}>
                <TopRightArrowIcon fill="#545B62" style={ICON_STYLES.arrowIcon} />
            </span>
            {source.dataPoint}
        </a>
    );

    return (
        <HeaderBodyBox
            title="Sector/Industry Outlook"
            subHeading={sectorName}
            headerRight={<AIGeneratedBadge />}
            className={styles.sectorOutlookContainer}
        >
            <div className={styles.sectorOutlookContent}>
                {renderSectorInsights()}

                <div className={styles.sectorOverview}>
                    <h3 className={styles.sectionTitle}>Sector Overview</h3>
                    <p className={styles.overviewText}>{sectorOutlook.sectorOverview}</p>
                </div>

                <div className={styles.trendsSection}>
                    <h3 className={styles.sectionTitle}>Recent Trends</h3>
                    <div className={styles.pillsContainer}>{sectorOutlook?.recentTrends?.map(renderTrendPill)}</div>
                </div>

                <div className={styles.comparisonSection}>
                    <div className={styles.comparisonColumn}>
                        <h3 className={styles.sectionTitle}>Growth Drivers:</h3>
                        <ul className={styles.driversList}>
                            {sectorOutlook?.growthDrivers?.map((driver, index) =>
                                renderListItem(driver, index, greenTick, styles.driverItem),
                            )}
                        </ul>
                    </div>

                    <div className={styles.comparisonColumn}>
                        <h3 className={styles.sectionTitle}>Key Risks:</h3>
                        <ul className={styles.risksList}>
                            {sectorOutlook?.keyRisks?.map((risk, index) => renderListItem(risk, index, redInfoFull, styles.riskItem))}
                        </ul>
                    </div>
                </div>
            </div>

            <div className={styles.sourcesSection}>
                <h3 className={styles.sectionTitle} style={{ marginBottom: '0px' }}>
                    Sources:
                </h3>
                <div className={styles.sourcesContainer}>{sectorOutlook?.sources?.map(renderSourcePill)}</div>
            </div>
        </HeaderBodyBox>
    );
};

export default SectorIndustryOutlookV2;
